import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { ErrorPage } from "./components/layouts/ErrorPage";
import { NotFoundPage } from "./components/layouts/NotFoundPage";
// Theme contract + global shell styles must load before any module that reads theme state.
import "../ui/styles/theme.css";
import { applyThemeToDocument, getInitialDarkMode } from "./lib/theme/atoms";
import { CertificationApp } from "../certification-app/CertificationApp";
import { AccountApp } from "../account-app/AccountApp";
import { isAccountAppHost, isCertificationAppHost } from "./lib/auth/app-origins";
import { initLogRocket } from "./lib/monitoring/logrocket";
import { initProductAnalytics } from "./lib/monitoring/product-analytics";

initLogRocket();
initProductAnalytics();

const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error, reset }) => (
    <ErrorPage error={error} reset={reset} />
  ),
  defaultNotFoundComponent: () => <NotFoundPage />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function MainApp() {
  return (
    <QueryClientProvider client={qc}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

// Sub-apps are structurally isolated from MainApp — each has its own
// router/route tree and no dashboard chrome (except MainApp). Chosen at
// boot, before any router is created, so e.g. an account.lionenergy.com
// visitor's app has zero knowledge of /workspace, /fleet, etc.
//
// Prefer isAccountAppHost() (hostname + :5174 port) over VITE_APP_TARGET alone:
// Google OAuth always returns to http://localhost:5174/… locally, and that
// tab must mount AccountApp even if the process wasn't started with --mode account.
const appTarget = import.meta.env.VITE_APP_TARGET;

const isAccountHost = isAccountAppHost() || appTarget === "account";
const isCertificationHost = isCertificationAppHost() || appTarget === "certification";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

applyThemeToDocument(getInitialDarkMode());

function RootApp() {
  if (isAccountHost) return <AccountApp />;
  if (isCertificationHost) return <CertificationApp />;
  return (
    <StrictMode>
      <MainApp />
    </StrictMode>
  );
}

createRoot(rootElement).render(<RootApp />);
